import type { useIntl } from '@leagueplatform/locales';
import type { MultiEnvironmentAction } from '../../types/multi-environment.types';

/**
 * Builds a "Copy to {environment}" menu item for either state a candidate can be in:
 * actionable (lookup settled, nothing there) or pending (lookup hasn't settled, so the item
 * is disabled with a tooltip rather than guessing).
 */
export function buildCopyToEnvironmentAction({
  id,
  environmentName,
  entityName,
  isEntitySaved,
  action,
  disabledTooltip,
  confirmationModal,
  formatMessage,
}: {
  id: string;
  environmentName: string;
  entityName: string;
  isEntitySaved: boolean;
  action?: MultiEnvironmentAction['action'];
  /**
   * Overrides the unsaved-changes tooltip for a candidate disabled for another reason.
   * Passing this without an `action` is how a pending item is built.
   */
  disabledTooltip?: string;
  /**
   * Shown before the copy runs when the target is dangerous to write to. Omitted for an item
   * that can't be actioned.
   */
  confirmationModal?: MultiEnvironmentAction['confirmationModal'];
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
}): MultiEnvironmentAction {
  return {
    label: formatMessage(
      { id: 'COPY_TO_ENVIRONMENT_ACTION' },
      { environment: environmentName },
    ),
    id,
    action,
    confirmationModal,
    disabledTooltip:
      disabledTooltip ??
      (!isEntitySaved
        ? formatMessage(
            { id: 'COPY_TO_ENVIRONMENT_UNSAVED_CHANGES' },
            { entity: entityName },
          )
        : undefined),
  };
}
